import { apiClient } from './api';

function unwrapData<T>(res: { success: boolean; data?: T; message?: string; error?: string }, fallbackMsg: string): T {
  if (!res.success || res.data === undefined) {
    throw new Error(res.message || res.error || fallbackMsg);
  }
  return res.data;
}

export type LeadJobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export type LeadSearchJob = {
  id: string;
  city: string;
  category: string;
  query: string;
  requestedLimit: number;
  requirePhone: boolean;
  requireWebsite: boolean;
  status: LeadJobStatus;
  totalFound: number;
  totalWithPhone: number;
  totalDuplicates: number;
  totalSaved: number;
  errorMessage: string;
  pagesQueued: number;
  pagesCrawled: number;
  pagesSkipped: number;
  pagesDiscovered: number;
  crawlErrors: number;
  currentDomain: string;
  currentUrl: string;
  seedUrls: string[];
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type LeadStatus = 'new' | 'contacted' | 'interested' | 'not_interested' | 'customer' | 'invalid';

export type LeadRow = {
  id: string;
  businessName: string;
  category: string;
  city: string;
  address: string;
  phone: string;
  website: string;
  source: string;
  sourceId: string;
  sourceUrl: string;
  sourceDomain: string;
  description: string;
  notes: string;
  status: LeadStatus;
  createdAt: string;
  updatedAt: string;
};

type PageResult<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function mapJob(raw: Record<string, unknown>): LeadSearchJob {
  return {
    id: String(raw.id ?? ''),
    city: String(raw.city ?? ''),
    category: String(raw.category ?? ''),
    query: String(raw.query ?? ''),
    requestedLimit: num(raw.requestedLimit ?? raw.requested_limit, 50),
    requirePhone: Boolean(raw.requirePhone ?? raw.require_phone),
    requireWebsite: Boolean(raw.requireWebsite ?? raw.require_website),
    status: (String(raw.status ?? 'pending') as LeadJobStatus) || 'pending',
    totalFound: num(raw.totalFound ?? raw.total_found),
    totalWithPhone: num(raw.totalWithPhone ?? raw.total_with_phone),
    totalDuplicates: num(raw.totalDuplicates ?? raw.total_duplicates),
    totalSaved: num(raw.totalSaved ?? raw.total_saved),
    errorMessage: String(raw.errorMessage ?? raw.error_message ?? ''),
    pagesQueued: num(raw.pagesQueued ?? raw.pages_queued),
    pagesCrawled: num(raw.pagesCrawled ?? raw.pages_crawled),
    pagesSkipped: num(raw.pagesSkipped ?? raw.pages_skipped),
    pagesDiscovered: num(raw.pagesDiscovered ?? raw.pages_discovered),
    crawlErrors: num(raw.crawlErrors ?? raw.crawl_errors),
    currentDomain: String(raw.currentDomain ?? raw.current_domain ?? ''),
    currentUrl: String(raw.currentUrl ?? raw.current_url ?? ''),
    seedUrls: Array.isArray(raw.seedUrls)
      ? (raw.seedUrls as unknown[]).map(String)
      : Array.isArray(raw.seed_urls)
        ? (raw.seed_urls as unknown[]).map(String)
        : [],
    startedAt: raw.startedAt ? String(raw.startedAt) : raw.started_at ? String(raw.started_at) : null,
    completedAt: raw.completedAt ? String(raw.completedAt) : raw.completed_at ? String(raw.completed_at) : null,
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? ''),
  };
}

function mapLead(raw: Record<string, unknown>): LeadRow {
  return {
    id: String(raw.id ?? ''),
    businessName: String(raw.businessName ?? raw.business_name ?? ''),
    category: String(raw.category ?? ''),
    city: String(raw.city ?? ''),
    address: String(raw.address ?? ''),
    phone: String(raw.phone ?? ''),
    website: String(raw.website ?? ''),
    source: String(raw.source ?? ''),
    sourceId: String(raw.sourceId ?? raw.source_id ?? ''),
    sourceUrl: String(raw.sourceUrl ?? raw.source_url ?? ''),
    sourceDomain: String(raw.sourceDomain ?? raw.source_domain ?? ''),
    description: String(raw.description ?? ''),
    notes: String(raw.notes ?? ''),
    status: (String(raw.status ?? 'new') as LeadStatus) || 'new',
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? ''),
  };
}

export const leadJobStatusLabel: Record<LeadJobStatus, string> = {
  pending: 'در صف',
  running: 'در حال پردازش',
  completed: 'تمام‌شده',
  failed: 'ناموفق',
  cancelled: 'لغوشده',
};

export const leadStatusLabel: Record<LeadStatus, string> = {
  new: 'جدید',
  contacted: 'تماس‌گرفته',
  interested: 'علاقه‌مند',
  not_interested: 'بی‌علاقه',
  customer: 'مشتری',
  invalid: 'نامعتبر',
};

export const leadService = {
  async listJobs(params: { page?: number; limit?: number; status?: string } = {}): Promise<PageResult<LeadSearchJob>> {
    const res = await apiClient.get<PageResult<Record<string, unknown>>>('/admin/leads/search-jobs', { params });
    const page = unwrapData(res, 'خطا در دریافت جاب‌های جستجو');
    return { ...page, data: (page.data ?? []).map(mapJob) };
  },
  async createJob(data: {
    city: string;
    category: string;
    query?: string;
    requestedLimit?: number;
    requirePhone?: boolean;
    requireWebsite?: boolean;
    seedUrls?: string[];
  }): Promise<LeadSearchJob> {
    const res = await apiClient.post<Record<string, unknown>>('/admin/leads/search-jobs', data);
    return mapJob(unwrapData(res, 'ایجاد جاب جستجو ناموفق بود'));
  },
  async getJob(id: string): Promise<LeadSearchJob> {
    const res = await apiClient.get<Record<string, unknown>>(`/admin/leads/search-jobs/${id}`);
    return mapJob(unwrapData(res, 'جاب یافت نشد'));
  },
  async cancelJob(id: string): Promise<LeadSearchJob> {
    const res = await apiClient.post<Record<string, unknown>>(`/admin/leads/search-jobs/${id}/cancel`, {});
    return mapJob(unwrapData(res, 'لغو جاب ناموفق بود'));
  },
  async listLeads(params: {
    page?: number;
    limit?: number;
    city?: string;
    category?: string;
    status?: string;
    source?: string;
    hasPhone?: boolean;
    q?: string;
  } = {}): Promise<PageResult<LeadRow>> {
    const res = await apiClient.get<PageResult<Record<string, unknown>>>('/admin/leads', {
      params: {
        ...params,
        hasPhone: params.hasPhone === undefined ? undefined : params.hasPhone ? 'true' : 'false',
      },
    });
    const page = unwrapData(res, 'خطا در دریافت لیدها');
    return { ...page, data: (page.data ?? []).map(mapLead) };
  },
  async getLead(id: string): Promise<LeadRow> {
    const res = await apiClient.get<Record<string, unknown>>(`/admin/leads/${id}`);
    return mapLead(unwrapData(res, 'لید یافت نشد'));
  },
  async patchLead(id: string, data: { status?: LeadStatus; notes?: string }): Promise<LeadRow> {
    const res = await apiClient.patch<Record<string, unknown>>(`/admin/leads/${id}`, data);
    return mapLead(unwrapData(res, 'ویرایش لید ناموفق بود'));
  },
  async deleteLead(id: string): Promise<void> {
    const res = await apiClient.delete<{ deleted?: number }>(`/admin/leads/${id}`);
    unwrapData(res, 'حذف لید ناموفق بود');
  },
  async bulkDeleteLeads(ids: string[]): Promise<number> {
    const res = await apiClient.post<{ deleted?: number }>('/admin/leads/bulk-delete', { ids });
    const data = unwrapData(res, 'حذف گروهی لیدها ناموفق بود');
    return Number(data.deleted ?? 0);
  },
};
